import axios, {AxiosError, AxiosResponse, HttpStatusCode} from 'axios';
import {pickBy} from 'lodash';
import {ApiErrorResponse, showCustomMessageToast} from '../api/utils';
import {showToastType} from '../contexts/toastContext/ToastContext';
import {
  FCAPIErrorResponse,
  FCLocalDirectory,
} from '../types/upload-new-eviidence/fcUploadFileTypes';
import mimeTypeObj from './mimeTypes';
import {GA_ERROR_CODES, GA_ERROR_TYPES} from '../constants/analyticsConstants';

export enum FCFileTransferState {
  UPLOADING = 'in-progress',
  PENDING = 'in-queue',
  FAILED = 'failed',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  RECONNECTING = 'reconnecting',
}

export enum FCFileTransferInternalState {
  INITTASK = 'Initialize',
  CONNECTED = 'Connected',
  CONNECTING = 'Connecting',
  CONNECTION_FAIL = 'Connection_Fail',
  UPLOADING = 'Uploading',
  PENDING = 'Pending',
  FAILED = 'Failed',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
  RECONNECTING = 'Reconnecting',
  SOFT_CANCELLED = 'SoftCancelled',
  HARD_CANCELLED = 'HardCancelled',
}

export const FC_BASE_URL = 'http://127.0.0.1:12680/rs/';
export const FC_AGENT_URL_SCHEME = 'filecatalyst://localhost';

export const FC_UPLOAD_STATIC_PATH = 'upload';

export const FC_HEART_BEAT_CYCLE_DELAY = 4000;
export const FC_HEART_BEAT_MAX_CYCLE_COUNT = 15;

export const FC_CONNECTION_API_CYCLE_DELAY = 1000;
export const FC_CONNECTION_API_MAX_CYCLE_COUNT = 3;

export const MAX_UPLOAD_QUAUE_SIZE = 4;
export const STATUS_POLLING_DURATION = 1000;

export const FC_MAX_RETRY_CYCLE = 10;
export const FC_API_RETRY_POLLING_DELAY = 5000;

export const FC_BLOCKED_FILE_EXTENSIONS = ['exe'];
export const FC_ZIP_FILE_EXTENSION = 'zip';
export const FC_FILE_TRANSFER_ROW_HEIGHT = 56;

export const getDateInUTCFormat = (lastModified: number) => {
  const date = new Date(lastModified);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  return `${year}${month}${day} ${hours}:${minutes} UTC`;
};

const byteSizeMap: Record<string, number> = {
  EB: 2 ** 60,
  PB: 2 ** 50,
  TB: 2 ** 40,
  GB: 2 ** 30,
  MB: 2 ** 20,
  KB: 2 ** 10,
  B: 2 ** 0,
};

export const getFileSize = (bytes: number) => {
  const byteUnit = 'B';

  const unit =
    Object.keys(byteSizeMap).find((key) => bytes >= byteSizeMap[key]) ||
    byteUnit;

  return `${(bytes / byteSizeMap[unit]).toFixed(unit === byteUnit ? 0 : 1)} ${unit}`;
};

export const getFileNameFromPath = (filePath: string) => {
  const parts = filePath.replace(/[\\/]+$/, '').split(/[\\/]/);
  return parts.pop();
};

export const isRemotePathAndLocalPathEqual = (
  remoteDirBasePath: string,
  remoteFilePath: string,
  localFilePath: string
) => {
  const normalize = (p: string) => {
    if (!p) return '';

    return p.replaceAll('\\', '/').toLowerCase();
  };

  const normalizedFull = normalize(localFilePath);
  const normalizedPartial = normalize(remoteFilePath).replace(
    remoteDirBasePath.toLowerCase(),
    ''
  );

  return normalizedFull.endsWith(normalizedPartial);
};

export const getRemoteFilePath = (filePath: string, folderPath: string) => {
  const normalizedFile = filePath.replaceAll('\\', '/');
  const normalizedFolder = folderPath.replaceAll('\\', '/');

  const folderParts = normalizedFolder.split('/').filter(Boolean);
  const rootDirName = folderParts.at(-1);

  const relativePart = normalizedFile
    .slice(normalizedFolder.length)
    .replace(/^\//, '');

  return `/${rootDirName}/${relativePart}`;
};

export const convertToDoubleBackslashes = (filePath: string) => {
  if (filePath.includes(':')) {
    return filePath.replaceAll('/', '\\');
  } else {
    return filePath;
  }
};

export const convertPathToSlashes = (filePath: string) => {
  return filePath.replaceAll('\\', '/');
};

export const isFilePathEqual = (path1: string, path2: string) => {
  const normalizePath = (p: string) => {
    if (!p) return '';

    return p.replaceAll('\\', '/');
  };

  const normalizedPath1 = normalizePath(path1);
  const normalizedPath2 = normalizePath(path2);

  return normalizedPath1 === normalizedPath2;
};

export const getRelativePathIfMatching = (
  filePath: string,
  dirPath: string
): string => {
  const normalizedFile = filePath.replaceAll('\\', '/');
  const normalizedDir = dirPath.replaceAll('\\', '/');

  if (normalizedFile.startsWith(normalizedDir)) {
    let relative = normalizedFile.slice(normalizedDir.length);

    if (relative.startsWith('/')) {
      relative = relative.slice(1);
    }

    const dirSegments = normalizedDir.split('/').filter(Boolean);
    const lastDirName = dirSegments.at(-1) ?? '';

    const result = lastDirName ? `/${lastDirName}/${relative}` : relative;
    return result;
  }

  return '';
};

export const getDirPathFromRemoteFilePath = (
  remoteFilePath: string
): string => {
  const lastSlashIndex = remoteFilePath.lastIndexOf('/');
  if (lastSlashIndex === -1) return remoteFilePath;
  return remoteFilePath.substring(0, lastSlashIndex) || '/';
};

export const getStoragePathParam = (remoteDirectoryBasePath: string) =>
  remoteDirectoryBasePath.replaceAll('/', '\\').replace('\\', '');

export const getFCFileExtension = (fileName: string): string =>
  (fileName?.split('.')?.pop() ?? '').toLowerCase();

export const getFileNameFromRemotePath = (
  remoteDirectoryBasePath: string,
  remotePath: string
) =>
  (remotePath ?? '')
    .replace(remoteDirectoryBasePath, '')
    .replaceAll('/', '\\')
    .replace('\\', '');

export const isFileExtensionBlocked = (file: FCLocalDirectory) =>
  (file?.isFile ?? false) &&
  FC_BLOCKED_FILE_EXTENSIONS.includes(getFCFileExtension(file?.name ?? ''));

export const isZipfile = (file: FCLocalDirectory) =>
  (file?.isFile ?? false) &&
  FC_ZIP_FILE_EXTENSION === getFCFileExtension(file?.name ?? '');

export const getMimeTypeFromFileName = (fileName: string) => {
  const filteredMimeTypeObj = pickBy(mimeTypeObj, (value) =>
    (value?.extensions ?? []).includes(getFCFileExtension(fileName))
  );
  return Object.keys(filteredMimeTypeObj)?.[0] ?? 'application/octet-stream';
};

export const fcDelay = (ms: number) =>
  new Promise((res) => setTimeout(res, ms));

export const isFCSuccess = (response: AxiosResponse) => {
  return response?.status === HttpStatusCode.Ok;
};

export const shouldFCRetry = (error: AxiosError) => {
  return error.response?.status !== HttpStatusCode.Ok;
};

export const isAPIRequestAborted = (error: AxiosError) => axios.isCancel(error);

export const showFCToastFromAxiosError = (
  showToast: showToastType,
  error: AxiosError,
  defaultErrorMessage: string
) => {
  if (isAPIRequestAborted(error)) return;

  const fcError: string = (error?.response?.data as FCAPIErrorResponse)?.reason
    ?.text;

  const aurError: string =
    (error?.response?.data as ApiErrorResponse)?.meta?.message ??
    ((error?.response?.data as ApiErrorResponse)?.validationErrors ?? [])
      .flatMap((item) => item?.message ?? '')
      .join(' ');

  showCustomMessageToast(
    showToast,
    fcError ?? (aurError.trim() || defaultErrorMessage),
    'error',
    {
      code: GA_ERROR_CODES.INTERNAL_SERVER_ERR_500,
      type: GA_ERROR_TYPES.FILE_UPLOAD_ERROR,
    }
  );
};
